import { CircularProgress } from '@mui/material'
import {
  Button,
  InitialsAvatar,
  pantryColors,
  pantryTypography,
} from '@pantry/ui'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type {
  CreateSegmentInput,
  SegmentCriteria,
  SegmentMatch,
  SegmentRuleField,
  SegmentRuleOp,
} from '../../../../types/loyaltySegments'
import {
  MAX_SEGMENT_RULES,
  type SegmentFieldOption,
  segmentEventDef,
  segmentFieldDef,
} from '../../../../types/loyaltySegments'
import { useCategoryList } from '../../../Category/hooks/useCategory'
import { LoyaltyProvider } from '../../context/LoyaltyProvider'
import { useLoyaltyContext } from '../../hooks/useLoyaltyContext'
import {
  getMarketingErrorMessage,
  useMarketingToast,
} from '../../marketing/hooks/useMarketingToast'
import {
  cardStyle,
  errorTextStyle,
  helpTextStyle,
  inputStyle,
  labelStyle,
} from '../../marketing/styles'
import {
  useCreateLoyaltySegment,
  useLoyaltySegmentsQuery,
  useSegmentPreviewMutation,
  useUpdateLoyaltySegment,
} from '../hooks/useLoyaltySegments'
import {
  type ConditionDraft,
  MAX_KEYWORD_LENGTH,
  SEGMENT_TEMPLATES,
  type SegmentTemplate,
  conditionError,
  draftToRule,
  emptyDraft,
  emptyDraftForEvent,
  emptyDraftForField,
  eventModeForPickerValue,
  eventOpOptions,
  eventPickerOptions,
  eventPickerValue,
  LEGACY_METRIC_PREFIX,
  numericOpOptions,
  pickerValueForDraft,
  ruleToDraft,
  sampleInitials,
  sampleMeta,
  sampleName,
  segmentFieldPickerGroups,
  selectOpIndex,
  selectOpOptions,
} from '../segmentBuilderRules'
import { avatarColor } from '../segmentMemberDisplay'

const p = pantryColors
const F = pantryTypography.fontFamily
const ACCENT = p.brand.primary

// Segment colour palette — the segment-builder mockup's `colorOptions`.
const COLOR_OPTIONS: { key: string; hex: string; label: string }[] = [
  { key: 'green', hex: '#1E7D4A', label: 'Green' },
  { key: 'blue', hex: '#1D4ED8', label: 'Blue' },
  { key: 'purple', hex: '#7C3AED', label: 'Purple' },
  { key: 'teal', hex: '#0F766E', label: 'Teal' },
  { key: 'orange', hex: '#EA580C', label: 'Orange' },
  { key: 'amber', hex: '#B45309', label: 'Amber' },
  { key: 'red', hex: '#DC2626', label: 'Red' },
  { key: 'indigo', hex: '#4338CA', label: 'Indigo' },
  { key: 'pink', hex: '#BE185D', label: 'Pink' },
  { key: 'gray', hex: '#4B5563', label: 'Gray' },
]

// Icon every segment created here carries (the audience-segments mockup's
// single people glyph); the builder no longer offers an icon picker.
const DEFAULT_ICON_KEY = 'users'

const PREVIEW_DEBOUNCE_MS = 500
// The sample list shows every customer the preview returned (the server caps
// the sample at 25) inside a scrollable box rather than truncating.
const PREVIEW_SAMPLE_MAX_HEIGHT = 240

// ── Small helpers ────────────────────────────────────────────────────────────

const numberFormatter = new Intl.NumberFormat('en-US')
const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const SaveIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
)

// Card shell used by the main column — header (title + sub) over content.
const BuilderCard: React.FC<{
  title: string
  subtitle: string
  children: React.ReactNode
}> = ({ title, subtitle, children }) => (
  <div style={cardStyle}>
    <div
      style={{
        padding: '16px 20px 14px',
        borderBottom: `1px solid ${p.gray[100]}`,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: p.gray[800] }}>
        {title}
      </div>
      <div style={{ fontSize: 12.5, color: p.gray[500], marginTop: 2 }}>
        {subtitle}
      </div>
    </div>
    <div style={{ padding: 20 }}>{children}</div>
  </div>
)

// Template pill — accent-light at rest, solid accent on hover.
const TemplatePill: React.FC<{
  template: SegmentTemplate
  onClick: () => void
}> = ({ template, onClick }) => {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '7px 14px',
        borderRadius: 999,
        border: `1px solid ${hovered ? ACCENT : p.brand.mid}`,
        background: hovered ? ACCENT : p.brand.light,
        color: hovered ? p.white : ACCENT,
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: F,
        transition: 'all .15s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: template.color,
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
      {template.name}
    </button>
  )
}

// Remove-condition button — red tint on hover.
const RemoveConditionButton: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      type="button"
      aria-label="Remove condition"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: 'none',
        background: hovered ? p.red[50] : 'transparent',
        borderRadius: 6,
        cursor: 'pointer',
        color: hovered ? p.red[700] : p.gray[400],
        padding: 6,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        transition: 'all .15s',
      }}
    >
      <svg
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={14}
        height={14}
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  )
}

// AND / OR pill between condition rows (mockup .condition-connector).
const Connector: React.FC<{ match: SegmentMatch }> = ({ match }) => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: p.gray[400],
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        background: p.gray[100],
        border: `1px solid ${p.gray[200]}`,
        borderRadius: 4,
        padding: '3px 10px',
        lineHeight: 1,
      }}
    >
      {match === 'all' ? 'AND' : 'OR'}
    </span>
  </div>
)

const rowLabelStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: p.gray[400],
  whiteSpace: 'nowrap',
}

// Condition rows are dense on purpose: smaller type + padding so a whole
// rule (field · operator · value · window) reads on one line.
const condInputStyle: React.CSSProperties = {
  ...inputStyle,
  fontSize: 12.5,
  padding: '6px 8px',
  borderRadius: 6,
}

// One breakdown row of the preview sidebar ("Total customers · 7,541").
const PreviewRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '9px 0',
      borderBottom: `1px solid ${p.gray[100]}`,
      fontSize: 12.5,
    }}
  >
    <span style={{ color: p.gray[500] }}>{label}</span>
    <span style={{ fontWeight: 600, color: p.gray[700] }}>{value}</span>
  </div>
)

// ── Condition row ────────────────────────────────────────────────────────────

interface ConditionRowProps {
  draft: ConditionDraft
  error: string | null
  options: SegmentFieldOption[]
  onPick: (value: string) => void
  onEvent: (value: string) => void
  onChange: (patch: Partial<ConditionDraft>) => void
  onSelectOp: (opIndex: number) => void
  onSetValues: (values: Array<string | number>) => void
  onRemove: () => void
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  draft,
  error,
  options,
  onPick,
  onEvent,
  onChange,
  onSelectOp,
  onSetValues,
  onRemove,
}) => {
  const def = segmentFieldDef(draft.field)
  const type = def?.type ?? 'number'
  const isEvent = draft.kind === 'event'
  const eventDef = isEvent ? segmentEventDef(draft.eventKey) : undefined
  const pickerGroups = segmentFieldPickerGroups(draft)
  const currentPicker = pickerGroups
    .flatMap((g) => g.options)
    .find((o) => o.value === pickerValueForDraft(draft))

  const windowInput = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        flex: '1.6 1 0',
        minWidth: 0,
      }}
    >
      <span style={rowLabelStyle}>in the last</span>
      <input
        style={{ ...condInputStyle, width: 60, flex: '0 0 60px' }}
        type="number"
        min={1}
        placeholder="All time"
        title="Optional — leave blank for all time"
        value={draft.windowDays}
        onChange={(e) => onChange({ windowDays: e.target.value })}
      />
      <span style={rowLabelStyle}>days</span>
    </div>
  )

  return (
    <div
      style={{
        background: p.gray[50],
        border: `1px solid ${p.gray[200]}`,
        borderRadius: 8,
        padding: '10px 12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: isEvent ? 6 : 8,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* Field / "Has done" / "Has not done" — the event picker is compact
            (mock .cond-timewin .cond-select) so the whole event row fits. */}
        <select
          style={{
            ...condInputStyle,
            width: 'auto',
            ...(isEvent
              ? { flex: '0 0 auto', minWidth: 90 }
              : { flex: '1.6 1 0', minWidth: 140 }),
          }}
          title={currentPicker?.hint}
          value={pickerValueForDraft(draft)}
          onChange={(e) => onPick(e.target.value)}
        >
          {pickerGroups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                >
                  {opt.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {isEvent ? (
          <>
            {/* [Event ▾] [keyword] [at least ▾] [N] times [in the last] [N] days */}
            <select
              style={{
                ...condInputStyle,
                width: 'auto',
                flex: '1.4 1 0',
                minWidth: 120,
              }}
              value={eventPickerValue(draft)}
              onChange={(e) => onEvent(e.target.value)}
            >
              <option value="">Select event…</option>
              {eventPickerOptions(draft).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {eventDef?.keyword && (
              <input
                style={{
                  ...condInputStyle,
                  width: 'auto',
                  flex: '1.2 1 0',
                  minWidth: 100,
                }}
                type="text"
                maxLength={MAX_KEYWORD_LENGTH}
                placeholder={
                  eventDef.keywordPlaceholder ?? 'containing keyword…'
                }
                value={draft.keyword}
                onChange={(e) => onChange({ keyword: e.target.value })}
              />
            )}
            {/* qualifier + count for BOTH Has done and Has not done (the
                latter is the negation, see draftToRule) */}
            <>
              <select
                style={{
                  ...condInputStyle,
                  width: 'auto',
                  flex: '0 0 auto',
                  minWidth: 90,
                }}
                value={draft.op}
                onChange={(e) =>
                  onChange({ op: e.target.value as SegmentRuleOp })
                }
              >
                {eventOpOptions(draft.op).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flex: '0 0 auto',
                }}
              >
                <input
                  style={{ ...condInputStyle, width: 60, flex: '0 0 60px' }}
                  type="number"
                  min={0}
                  step={1}
                  placeholder="#"
                  value={draft.value}
                  onChange={(e) => onChange({ value: e.target.value })}
                />
                {draft.op === 'between' && (
                  <>
                    <span style={rowLabelStyle}>and</span>
                    <input
                      style={{
                        ...condInputStyle,
                        width: 60,
                        flex: '0 0 60px',
                      }}
                      type="number"
                      min={0}
                      step={1}
                      placeholder="#"
                      value={draft.value2}
                      onChange={(e) => onChange({ value2: e.target.value })}
                    />
                  </>
                )}
                <span style={rowLabelStyle}>times</span>
              </div>
            </>
            {windowInput}
          </>
        ) : type === 'bool' ? (
          <>
            <select
              style={{ ...condInputStyle, width: 'auto', flex: '0 1 90px' }}
              value="is"
              disabled
            >
              <option value="is">is</option>
            </select>
            <select
              style={{ ...condInputStyle, width: 'auto', flex: '1 1 100px' }}
              value={draft.value === '0' ? '0' : '1'}
              onChange={(e) => onChange({ value: e.target.value })}
            >
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </>
        ) : type === 'select' ? (
          <select
            style={{
              ...condInputStyle,
              width: 'auto',
              flex: '1.2 1 0',
              minWidth: 100,
            }}
            value={String(selectOpIndex(draft))}
            onChange={(e) => onSelectOp(Number(e.target.value))}
          >
            {selectOpOptions(draft).map((opt, i) => (
              <option key={opt.label} value={String(i)}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <>
            <select
              style={{
                ...condInputStyle,
                width: 'auto',
                flex: '1.2 1 0',
                minWidth: 100,
              }}
              value={draft.op}
              onChange={(e) =>
                onChange({ op: e.target.value as SegmentRuleOp })
              }
            >
              {numericOpOptions(draft.op).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {draft.op === 'between' ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flex: '1.8 1 0',
                  minWidth: 160,
                }}
              >
                <input
                  style={{ ...condInputStyle, flex: 1, minWidth: 60 }}
                  type="number"
                  min={0}
                  placeholder="Min"
                  value={draft.value}
                  onChange={(e) => onChange({ value: e.target.value })}
                />
                <span style={rowLabelStyle}>and</span>
                <input
                  style={{ ...condInputStyle, flex: 1, minWidth: 60 }}
                  type="number"
                  min={0}
                  placeholder="Max"
                  value={draft.value2}
                  onChange={(e) => onChange({ value2: e.target.value })}
                />
              </div>
            ) : (
              <input
                style={{
                  ...condInputStyle,
                  width: 'auto',
                  flex: '1.8 1 0',
                  minWidth: 100,
                }}
                type="number"
                min={0}
                placeholder="Enter value…"
                value={draft.value}
                onChange={(e) => onChange({ value: e.target.value })}
              />
            )}
          </>
        )}

        {type === 'select' && !isEvent && (
          <div
            style={{
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              alignItems: 'center',
              flex: '1.8 1 0',
              minWidth: 100,
            }}
          >
            {draft.field === 'product_category' && options.length === 0 ? (
              <span style={{ fontSize: 12, color: p.gray[400] }}>
                No categories found for this store yet.
              </span>
            ) : draft.selectMulti ? (
              <>
                {/* "is any of": a dropdown adds a value; picked values sit as
                  removable chips beside it. */}
                {draft.values.map((value) => {
                  const label =
                    options.find((o) => o.value === value)?.label ??
                    String(value)
                  return (
                    <span
                      key={String(value)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 8px 4px 11px',
                        borderRadius: 999,
                        border: `1px solid ${ACCENT}`,
                        background: p.brand.light,
                        color: ACCENT,
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: F,
                      }}
                    >
                      {label}
                      <button
                        type="button"
                        aria-label={`Remove ${label}`}
                        onClick={() =>
                          onSetValues(draft.values.filter((v) => v !== value))
                        }
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: ACCENT,
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 13,
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )
                })}
                <select
                  style={{ ...condInputStyle, width: 'auto', minWidth: 140 }}
                  value=""
                  onChange={(e) => {
                    const picked = options.find(
                      (o) => String(o.value) === e.target.value,
                    )
                    if (picked && !draft.values.includes(picked.value))
                      onSetValues([...draft.values, picked.value])
                  }}
                >
                  <option value="">
                    {draft.values.length ? 'Add another…' : 'Select…'}
                  </option>
                  {options
                    .filter((o) => !draft.values.includes(o.value))
                    .map((o) => (
                      <option key={String(o.value)} value={String(o.value)}>
                        {o.label}
                      </option>
                    ))}
                </select>
              </>
            ) : (
              <select
                style={{
                  ...condInputStyle,
                  width: 'auto',
                  flex: '1 1 0',
                  minWidth: 100,
                }}
                value={draft.values.length ? String(draft.values[0]) : ''}
                onChange={(e) => {
                  const picked = options.find(
                    (o) => String(o.value) === e.target.value,
                  )
                  onSetValues(picked ? [picked.value] : [])
                }}
              >
                <option value="">Select…</option>
                {options.map((o) => (
                  <option key={String(o.value)} value={String(o.value)}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Field rows with a window (e.g. Total Orders) show it too — a
            stored window_days is sent, so it must be visible and clearable. */}
        {!isEvent && def?.window && windowInput}

        <div style={{ marginLeft: 'auto' }}>
          <RemoveConditionButton onClick={onRemove} />
        </div>
      </div>

      {error && <div style={{ ...errorTextStyle, marginTop: 6 }}>{error}</div>}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

const SegmentBuilderPageContent: React.FC = () => {
  const { storeId } = useLoyaltyContext()
  const { storeId: routeStoreId, segmentId } = useParams<{
    storeId: string
    segmentId?: string
  }>()
  const navigate = useNavigate()
  const { showToast, toastNode } = useMarketingToast()

  const backPath = `/s/${routeStoreId}/loyalty/segments`
  const isEdit = Boolean(segmentId)

  const { data: segments, isLoading: segmentsLoading } =
    useLoyaltySegmentsQuery(storeId)
  const editTarget = isEdit
    ? ((segments ?? []).find((s) => s.id === segmentId) ?? null)
    : null

  const createMutation = useCreateLoyaltySegment(storeId)
  const updateMutation = useUpdateLoyaltySegment(storeId)
  const isSaving = createMutation.isPending || updateMutation.isPending

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  // '' = "No color selected" (the server accepts a blank colour).
  const [color, setColor] = useState('')
  const [match, setMatch] = useState<SegmentMatch>('all')
  const [conditions, setConditions] = useState<ConditionDraft[]>([])

  // Edit mode hydrates the form once the segment document is in cache.
  const [hydratedId, setHydratedId] = useState<string | null>(null)
  useEffect(() => {
    if (!editTarget || hydratedId === editTarget.id) return
    setName(editTarget.name)
    setDescription(editTarget.description ?? '')
    setColor(editTarget.color || '')
    setMatch(editTarget.criteria?.match ?? 'all')
    setConditions((editTarget.criteria?.rules ?? []).map(ruleToDraft))
    setHydratedId(editTarget.id)
  }, [editTarget, hydratedId])

  // A segment saved with a colour outside the palette still shows (and
  // keeps) its colour: prepend it as an extra swatch.
  const colorOptions = useMemo(() => {
    if (!color || COLOR_OPTIONS.some((c) => c.hex === color)) {
      return COLOR_OPTIONS
    }
    return [{ key: color, hex: color, label: 'Custom' }, ...COLOR_OPTIONS]
  }, [color])
  const selectedColor = colorOptions.find((c) => c.hex === color)

  const conditionErrors = conditions.map(conditionError)
  const rulesValid =
    conditions.length > 0 && conditionErrors.every((e) => e === null)
  const canSubmit = Boolean(name.trim()) && rulesValid && !isSaving

  // Serialized criteria snapshot — '' while the conditions are incomplete.
  // Doubles as the debounce key: the preview effect re-arms when it changes.
  const criteriaKey = rulesValid
    ? JSON.stringify({
        match,
        rules: conditions.map(draftToRule),
      } satisfies SegmentCriteria)
    : ''

  // ── Live preview (debounced 500ms) ─────────────────────────────────────────
  const previewMutation = useSegmentPreviewMutation()
  const { mutate: runPreview, reset: resetPreview } = previewMutation
  useEffect(() => {
    if (!storeId || !criteriaKey) {
      resetPreview()
      return
    }
    const id = window.setTimeout(
      () =>
        runPreview({
          storeId,
          criteria: JSON.parse(criteriaKey) as SegmentCriteria,
        }),
      PREVIEW_DEBOUNCE_MS,
    )
    return () => window.clearTimeout(id)
  }, [storeId, criteriaKey, runPreview, resetPreview])

  const preview = previewMutation.data

  // The store's customer count comes back with every preview; remember the
  // last one so "Total customers" stays populated while the rules are
  // incomplete (the mockup always shows it). Written from an effect, not
  // during render, so it never mutates the ref as a render side-effect.
  const totalCustomersRef = useRef<number | null>(null)
  useEffect(() => {
    if (preview?.total_members != null) {
      totalCustomersRef.current = preview.total_members
    }
  }, [preview?.total_members])
  const totalCustomers = preview?.total_members ?? totalCustomersRef.current

  const matchPct = useMemo(() => {
    if (!preview || !preview.total_members) return null
    return (preview.count / preview.total_members) * 100
  }, [preview])

  // ── Condition mutations ────────────────────────────────────────────────────
  const updateCondition = (index: number, patch: Partial<ConditionDraft>) =>
    setConditions((prev) =>
      prev.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)),
    )

  // The field <select>: a catalog field, or "Has done" / "Has not done".
  const pickField = (index: number, value: string) =>
    setConditions((prev) =>
      prev.map((draft, i) => {
        if (i !== index) return draft
        const eventMode = eventModeForPickerValue(value)
        if (eventMode) {
          // Keep the row's own id across the kind swap — it's still "the
          // same row" the user is editing, and the React key must stay put
          // so the row doesn't remount mid-interaction.
          if (draft.kind !== 'event')
            return { ...emptyDraftForEvent(eventMode), id: draft.id }
          // Flipping has-done ↔ has-not-done keeps the event, keyword and
          // window; a has-not-done row re-enters has-done as "at least 1".
          // Legacy "between" has no single-rule negation (draftToRule would
          // send just "fewer than N"), so it restarts as "at least 1".
          const resetCount =
            (eventMode === 'has_done' && draft.value === '0') ||
            (eventMode === 'has_not_done' && draft.op === 'between')
          return {
            ...draft,
            eventMode,
            ...(resetCount ? { op: 'gte' as const, value: '1' } : {}),
          }
        }
        const field = value as SegmentRuleField
        if (draft.kind === 'event')
          return { ...emptyDraftForField(field), id: draft.id }
        if (field === draft.field) return draft
        // Changing the field resets the parts the new field doesn't share;
        // same-type number fields keep the comparable inputs.
        const next = { ...emptyDraftForField(field), id: draft.id }
        const prevDef = segmentFieldDef(draft.field)
        const nextDef = segmentFieldDef(field)
        if (prevDef?.type === 'number' && nextDef?.type === 'number') {
          next.op = draft.op
          next.value = draft.value
          next.value2 = draft.value2
          if (nextDef.window) next.windowDays = draft.windowDays
        }
        return next
      }),
    )

  // The event <select> on an event row.
  const pickEvent = (index: number, value: string) =>
    setConditions((prev) =>
      prev.map((draft, i) => {
        if (i !== index) return draft
        if (value.startsWith(LEGACY_METRIC_PREFIX)) return draft
        const eventDef = segmentEventDef(value)
        return {
          ...draft,
          eventKey: value,
          metric: '',
          keyword: eventDef?.keyword ? draft.keyword : '',
        }
      }),
    )

  const addCondition = () =>
    setConditions((prev) =>
      prev.length >= MAX_SEGMENT_RULES ? prev : [...prev, emptyDraft()],
    )
  const removeCondition = (index: number) =>
    setConditions((prev) => prev.filter((_, i) => i !== index))

  // "is" / "is not" hold exactly one value, "is any of" a list — the row's
  // dropdown(s) hand back the whole list.
  const setValues = (index: number, values: Array<string | number>) =>
    setConditions((prev) =>
      prev.map((draft, i) =>
        i !== index
          ? draft
          : {
              ...draft,
              values: draft.selectMulti ? values : values.slice(0, 1),
            },
      ),
    )

  // Select-op change: switching to a single-choice op keeps only the first chip.
  const selectOp = (index: number, opIndex: number) =>
    setConditions((prev) =>
      prev.map((draft, i) => {
        if (i !== index) return draft
        const opDef = selectOpOptions(draft)[opIndex]
        if (!opDef) return draft
        return {
          ...draft,
          op: opDef.value,
          selectMulti: opDef.multi,
          values: opDef.multi ? draft.values : draft.values.slice(0, 1),
        }
      }),
    )

  // Purchased Category chips come from the store's category tree (flattened,
  // de-duplicated, sorted). Values on a stored rule that no longer exist as
  // categories are kept as chips so editing never silently drops them.
  const { data: categoryTree } = useCategoryList(storeId)
  const categoryOptions = useMemo<SegmentFieldOption[]>(() => {
    const names = new Set<string>()
    const walk = (items: typeof categoryTree) => {
      for (const item of items ?? []) {
        if (item.name) names.add(item.name)
        if (item.children?.length) walk(item.children)
      }
    }
    walk(categoryTree)
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }))
  }, [categoryTree])
  const optionsFor = (draft: ConditionDraft): SegmentFieldOption[] => {
    const def = segmentFieldDef(draft.field)
    if (draft.field !== 'product_category') return def?.options ?? []
    const known = new Set(categoryOptions.map((o) => o.value))
    const extra = draft.values
      .filter((v) => !known.has(v))
      .map((v) => ({ value: v, label: String(v) }))
    return [...extra, ...categoryOptions]
  }

  const applyTemplate = (template: SegmentTemplate) => {
    setName(template.name)
    setDescription(template.description)
    setColor(template.color)
    setMatch(template.match)
    setConditions(template.rules.map(ruleToDraft))
    showToast('Template applied')
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    const input: CreateSegmentInput = {
      name: name.trim(),
      description: description.trim(),
      color,
      icon_key: editTarget?.icon_key || DEFAULT_ICON_KEY,
      criteria: { match, rules: conditions.map(draftToRule) },
    }
    try {
      if (isEdit && editTarget) {
        await updateMutation.mutateAsync({ segmentId: editTarget.id, input })
      } else {
        await createMutation.mutateAsync(input)
      }
      navigate(backPath)
    } catch (error) {
      showToast(getMarketingErrorMessage(error), 'error')
    }
  }

  // Edit deep-link while the list is loading / after an unknown id.
  if (isEdit && !editTarget) {
    return (
      <div style={{ padding: '60px 24px', textAlign: 'center', fontFamily: F }}>
        {segmentsLoading ? (
          <>
            <CircularProgress
              size={20}
              thickness={5}
              sx={{ color: p.brand.primary }}
            />
            <div style={{ marginTop: 12, fontSize: 13, color: p.gray[500] }}>
              Loading segment…
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: p.gray[700],
                marginBottom: 6,
              }}
            >
              Segment not found
            </div>
            <div style={{ fontSize: 13, color: p.gray[500], marginBottom: 16 }}>
              It may have been deleted, or the link is out of date.
            </div>
            <Button variant="ghost" onClick={() => navigate(backPath)}>
              Back to Segments
            </Button>
          </>
        )}
      </div>
    )
  }

  const hasEstimate = Boolean(criteriaKey) && Boolean(preview)

  return (
    <div
      style={{
        minHeight: '100%',
        background: p.gray[50],
        fontFamily: F,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Sticky page header ─────────────────────────────────────────────── */}
      <div
        style={{
          background: p.white,
          borderBottom: `1px solid ${p.gray[200]}`,
          padding: '14px 28px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          position: 'sticky',
          top: 0,
          zIndex: 50,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => navigate(backPath)}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: p.gray[500],
            fontSize: 13,
            fontWeight: 600,
            fontFamily: F,
            padding: '6px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>‹</span>
          Segments
        </button>
        <div style={{ width: 1, height: 24, background: p.gray[200] }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: p.gray[800] }}>
            {isEdit ? 'Edit Segment' : 'Create Segment'}
          </div>
          <div style={{ fontSize: 12, color: p.gray[500] }}>
            {isEdit && editTarget
              ? `Editing: ${editTarget.name}`
              : 'Define rules to automatically group your customers'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button variant="ghost" onClick={() => navigate(backPath)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<SaveIcon />}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Segment'}
          </Button>
        </div>
      </div>

      {/* ── Body: main column + sticky preview sidebar ─────────────────────── */}
      <div
        style={{
          // Mock .builder-main / .builder-sidebar: the form takes every pixel
          // the preview column (fixed 300px) leaves, with no page max-width —
          // that width is what keeps a whole condition on one line.
          display: 'flex',
          gap: 24,
          alignItems: 'flex-start',
          flexWrap: 'nowrap',
          padding: '24px 28px',
          width: '100%',
          boxSizing: 'border-box',
          flex: 1,
        }}
      >
        <div
          style={{
            flex: '1 1 0',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/* a. Templates */}
          {!isEdit && (
            <BuilderCard
              title="Start from a template"
              subtitle="Common audience patterns — customize after applying"
            >
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {SEGMENT_TEMPLATES.map((template) => (
                  <TemplatePill
                    key={template.name}
                    template={template}
                    onClick={() => applyTemplate(template)}
                  />
                ))}
              </div>
            </BuilderCard>
          )}

          {/* b. Segment info */}
          <BuilderCard
            title="Segment Info"
            subtitle="Give your segment a name and classify it"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <span style={labelStyle}>
                    Segment Name <span style={{ color: p.red[700] }}>*</span>
                  </span>
                  <input
                    style={inputStyle}
                    value={name}
                    placeholder="e.g. High-Value Flower Buyers"
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <span style={labelStyle}>
                    Color{' '}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 400,
                        color: p.gray[400],
                      }}
                    >
                      — optional
                    </span>
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      gap: 6,
                      flexWrap: 'nowrap',
                      alignItems: 'center',
                      padding: '2px 0',
                    }}
                  >
                    {colorOptions.map((opt) => {
                      const selected = color === opt.hex
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          aria-label={`Color ${opt.label}`}
                          aria-pressed={selected}
                          title={opt.label}
                          onClick={() => setColor(selected ? '' : opt.hex)}
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            border: `2px solid ${selected ? p.white : 'transparent'}`,
                            outline: selected ? `2px solid ${opt.hex}` : 'none',
                            outlineOffset: 1,
                            background: opt.hex,
                            cursor: 'pointer',
                            padding: 0,
                            boxSizing: 'border-box',
                            flexShrink: 0,
                          }}
                        />
                      )
                    })}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 6,
                      marginLeft: 4,
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: selectedColor?.hex ?? p.gray[200],
                        flexShrink: 0,
                        transition: 'background .2s',
                      }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        color: selectedColor?.hex ?? p.gray[400],
                      }}
                    >
                      {selectedColor?.label ?? 'No color selected'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <span style={labelStyle}>Description</span>
                <textarea
                  rows={3}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    minHeight: 72,
                    lineHeight: 1.5,
                    fontFamily: F,
                  }}
                  value={description}
                  placeholder="Describe who this segment targets and how it will be used…"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </BuilderCard>

          {/* c. Conditions */}
          <BuilderCard
            title="Conditions"
            subtitle="Customers must match these rules to be included"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                fontSize: 13,
                color: p.gray[600],
                marginBottom: 16,
              }}
            >
              <span>Match</span>
              <div
                style={{
                  display: 'inline-flex',
                  border: `1px solid ${p.gray[200]}`,
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                {(
                  [
                    { value: 'all', label: 'ALL' },
                    { value: 'any', label: 'ANY' },
                  ] as { value: SegmentMatch; label: string }[]
                ).map((opt) => {
                  const active = match === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setMatch(opt.value)}
                      style={{
                        border: 'none',
                        padding: '5px 12px',
                        fontSize: 12.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: F,
                        background: active ? ACCENT : p.white,
                        color: active ? p.white : p.gray[500],
                        transition: 'all .12s',
                      }}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
              <span>of the following conditions</span>
            </div>

            {conditions.length === 0 ? (
              <div
                style={{
                  border: `1.5px dashed ${p.gray[200]}`,
                  borderRadius: 8,
                  padding: '24px 16px',
                  textAlign: 'center',
                  fontSize: 13,
                  color: p.gray[400],
                }}
              >
                No conditions yet — click &quot;Add condition&quot; to start
                filtering
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {conditions.map((draft, index) => (
                  // `draft.id` — stable across reorders/deletes, unlike the
                  // array index — so React keeps each row's own DOM identity.
                  <React.Fragment key={draft.id}>
                    {index > 0 && <Connector match={match} />}
                    <ConditionRow
                      draft={draft}
                      error={conditionErrors[index]}
                      options={optionsFor(draft)}
                      onPick={(value) => pickField(index, value)}
                      onEvent={(value) => pickEvent(index, value)}
                      onChange={(patch) => updateCondition(index, patch)}
                      onSelectOp={(opIndex) => selectOp(index, opIndex)}
                      onSetValues={(values) => setValues(index, values)}
                      onRemove={() => removeCondition(index)}
                    />
                  </React.Fragment>
                ))}
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={addCondition}
                disabled={conditions.length >= MAX_SEGMENT_RULES}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor:
                    conditions.length >= MAX_SEGMENT_RULES
                      ? 'default'
                      : 'pointer',
                  color:
                    conditions.length >= MAX_SEGMENT_RULES
                      ? p.gray[300]
                      : ACCENT,
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: F,
                  padding: '8px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v8M8 12h8" />
                </svg>
                Add condition
              </button>
              {conditions.length >= MAX_SEGMENT_RULES && (
                <div style={helpTextStyle}>
                  Segments support up to {MAX_SEGMENT_RULES} rules.
                </div>
              )}
            </div>
          </BuilderCard>
        </div>

        {/* ── Audience preview sidebar ───────────────────────────────────────── */}
        <div
          style={{
            flex: '0 0 300px',
            width: 300,
            position: 'sticky',
            top: 90,
            alignSelf: 'flex-start',
          }}
        >
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            <div
              style={{
                padding: '14px 18px',
                borderBottom: `1px solid ${p.gray[100]}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{ fontSize: 13.5, fontWeight: 700, color: p.gray[800] }}
              >
                Audience Preview
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11,
                  fontWeight: 600,
                  color: ACCENT,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: ACCENT,
                    display: 'inline-block',
                  }}
                />
                Live estimate
              </div>
            </div>

            <div style={{ padding: 18 }}>
              <div style={{ textAlign: 'center', padding: '6px 0 10px' }}>
                <div
                  style={{
                    fontSize: 36,
                    fontWeight: 800,
                    color: p.gray[800],
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {hasEstimate && preview
                    ? numberFormatter.format(preview.count)
                    : '—'}
                </div>
                <div
                  style={{ fontSize: 12.5, color: p.gray[500], marginTop: 4 }}
                >
                  matching customers
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginTop: 4,
                    color: hasEstimate ? ACCENT : p.gray[300],
                  }}
                >
                  {!criteriaKey
                    ? 'Add conditions to estimate'
                    : previewMutation.isError
                      ? 'Could not evaluate these conditions'
                      : !preview
                        ? 'Estimating…'
                        : matchPct !== null
                          ? `${Math.round(matchPct)}% of total customers`
                          : ' '}
                </div>
              </div>

              <div
                style={{
                  height: 6,
                  borderRadius: 999,
                  background: p.gray[100],
                  margin: '4px 0 16px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    borderRadius: 999,
                    background: ACCENT,
                    width: `${Math.min(100, hasEstimate ? (matchPct ?? 0) : 0)}%`,
                    transition: 'width .3s',
                  }}
                />
              </div>

              <div style={{ borderTop: `1px solid ${p.gray[100]}` }}>
                <PreviewRow
                  label="Total customers"
                  value={
                    totalCustomers != null
                      ? numberFormatter.format(totalCustomers)
                      : '—'
                  }
                />
                <PreviewRow
                  label="Match rate"
                  value={
                    hasEstimate && matchPct !== null
                      ? `${matchPct < 10 ? matchPct.toFixed(1) : Math.round(matchPct)}%`
                      : '—'
                  }
                />
                <PreviewRow
                  label="Avg LTV"
                  value={
                    hasEstimate && preview
                      ? moneyFormatter.format(preview.avg_ltv ?? 0)
                      : '—'
                  }
                />
                <PreviewRow
                  label="Avg orders"
                  value={
                    hasEstimate && preview
                      ? (preview.avg_orders ?? 0).toFixed(1)
                      : '—'
                  }
                />
              </div>

              {hasEstimate && preview && preview.sample.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: p.gray[400],
                      marginBottom: 6,
                    }}
                  >
                    Sample customers
                    {preview.count > preview.sample.length && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontWeight: 500,
                          textTransform: 'none',
                          letterSpacing: 0,
                        }}
                      >
                        · showing {preview.sample.length} of{' '}
                        {preview.count.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      maxHeight: PREVIEW_SAMPLE_MAX_HEIGHT,
                      overflowY: 'auto',
                      paddingRight: 4,
                    }}
                  >
                    {preview.sample.map((customer, i) => {
                      const customerName = sampleName(customer)
                      const meta = sampleMeta(customer)
                      return (
                        <div
                          key={customer.customer_id ?? customer.email ?? i}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '6px 0',
                          }}
                        >
                          <InitialsAvatar
                            initials={sampleInitials(customer)}
                            color={avatarColor(customerName)}
                            size="sm"
                          />
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12.5,
                                fontWeight: 500,
                                color: p.gray[700],
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {customerName}
                            </div>
                            {meta && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: p.gray[400],
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {meta}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tips — sits under the preview in the sidebar (mockup tips card) */}
          <div style={{ ...cardStyle, padding: 16, marginTop: 14 }}>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 700,
                color: p.gray[700],
                marginBottom: 8,
              }}
            >
              <span aria-hidden>💡</span> Tips
            </div>
            <div
              style={{
                fontSize: 12,
                color: p.gray[500],
                lineHeight: 1.6,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div>
                • Use <strong>ALL</strong> to narrow down (stricter match)
              </div>
              <div>
                • Use <strong>ANY</strong> to broaden (more customers)
              </div>
              <div>• Segments update automatically every hour</div>
              <div>• Combine with campaigns to target specific groups</div>
            </div>
          </div>
        </div>
      </div>

      {toastNode}
    </div>
  )
}

// Keyed by segmentId so navigating edit → edit (a different segment) or
// edit → create remounts the content instead of reusing state left over
// from the previous draft (conditions, hydratedId, preview, …).
export const SegmentBuilderPage: React.FC = () => {
  const { segmentId } = useParams<{ segmentId?: string }>()
  return (
    <LoyaltyProvider>
      <SegmentBuilderPageContent key={segmentId ?? 'new'} />
    </LoyaltyProvider>
  )
}

export default SegmentBuilderPage
